// Identify which FTMS machine is on the other side of a fresh GATT connection.
//
// The FTMS spec does NOT encode equipment type in the Fitness Machine Feature
// bitmask (that field describes capabilities — does it report power, cadence,
// etc.). The type IS encoded in which Data characteristic the GATT server
// exposes. So: enumerate characteristics, look for the first known data UUID,
// and use the registry entry. Read 0x2ACC separately for capability hints.

import { FITNESS_MACHINE_FEATURE_UUID, FTMS_SERVICE_UUID } from "./constants";
import { EMPTY_FEATURES, parseFeature } from "./feature";
import { EQUIPMENT_ENTRIES } from "./registry";
import type { ClassifiedDevice } from "./types";

interface GattServerLike {
  getPrimaryService(uuid: number): Promise<GattServiceLike>;
}
interface GattServiceLike {
  getCharacteristics(): Promise<GattCharacteristicLike[]>;
  getCharacteristic(uuid: number): Promise<GattCharacteristicLike>;
}
interface GattCharacteristicLike {
  uuid: string;
  readValue(): Promise<DataView>;
}

/**
 * Normalise a 16-bit assigned number into the canonical lowercase 128-bit
 * UUID string used by Web Bluetooth (e.g. 0x2AD2 → "00002ad2-0000-1000-...").
 */
const toLongUuid = (short: number): string => {
  const hex = short.toString(16).padStart(4, "0").toLowerCase();
  return `0000${hex}-0000-1000-8000-00805f9b34fb`;
};

export async function classifyServer(
  server: GattServerLike,
): Promise<ClassifiedDevice> {
  const svc = await server.getPrimaryService(FTMS_SERVICE_UUID);
  const chars = await svc.getCharacteristics();
  const presentUuids = new Set(chars.map((c) => c.uuid.toLowerCase()));

  for (const descriptor of EQUIPMENT_ENTRIES) {
    if (descriptor.status !== "phase1") continue;
    const target = toLongUuid(descriptor.dataCharUuid);
    if (!presentUuids.has(target)) continue;

    let features = EMPTY_FEATURES;
    try {
      const featureChar = await svc.getCharacteristic(
        FITNESS_MACHINE_FEATURE_UUID,
      );
      features = parseFeature(await featureChar.readValue());
    } catch {
      // Optional characteristic on some devices; missing is fine.
    }

    return {
      type: descriptor.type,
      dataCharUuid: descriptor.dataCharUuid,
      features,
    };
  }

  throw new Error(
    "FTMS device exposes no supported data characteristic. " +
      "If this is a Concept2 PM5, update its firmware to version 22 or newer.",
  );
}

// Exported for tests.
export const _toLongUuid = toLongUuid;
